// Modern Business Card Detection with Real-time Processing
// Inspired by modern scanning apps like Popl

export class ModernCardDetection {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.detectionCanvas = null;
    this.detectionCtx = null;
    this.isProcessing = false;
    this.lastDetection = null;
    this.detectionHistory = [];
    this.stabilityThreshold = 5;
    this.confidenceThreshold = 0.6;
  }

  initCanvas(width, height) {
    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
      this.ctx = this.canvas.getContext('2d');
      this.detectionCanvas = document.createElement('canvas');
      this.detectionCtx = this.detectionCanvas.getContext('2d');
    }
    
    this.canvas.width = width;
    this.canvas.height = height;
    this.detectionCanvas.width = Math.floor(width / 4); // Reduce for faster processing
    this.detectionCanvas.height = Math.floor(height / 4);
    
    return this.canvas;
  }

  // Convert to grayscale for faster processing
  toGrayscale(imageData) {
    const data = imageData.data;
    const grayData = new Uint8ClampedArray(imageData.width * imageData.height);
    
    for (let i = 0; i < data.length; i += 4) {
      const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      grayData[i / 4] = gray;
    }
    
    return {
      data: grayData,
      width: imageData.width,
      height: imageData.height
    };
  }

  // Advanced edge detection optimized for business cards
  detectCardEdges(grayData) {
    const { data, width, height } = grayData;
    const edges = new Uint8ClampedArray(width * height);
    
    // Sobel operator with optimized thresholds for business cards
    const sobelX = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
    const sobelY = [-1, -2, -1, 0, 0, 0, 1, 2, 1];
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let gx = 0, gy = 0;
        
        for (let i = 0; i < 9; i++) {
          const dx = (i % 3) - 1;
          const dy = Math.floor(i / 3) - 1;
          const idx = (y + dy) * width + (x + dx);
          const pixel = data[idx];
          
          gx += pixel * sobelX[i];
          gy += pixel * sobelY[i];
        }
        
        const magnitude = Math.sqrt(gx * gx + gy * gy);
        edges[y * width + x] = magnitude > 30 ? 255 : 0;
      }
    }
    
    return { data: edges, width, height };
  }

  // Find rectangular contours using improved Hough transform
  findRectangularContours(edges) {
    const { data, width, height } = edges;
    const lines = this.detectLines(data, width, height);
    const rectangles = this.findRectanglesFromLines(lines, width, height);
    
    return rectangles.sort((a, b) => b.confidence - a.confidence);
  }

  detectLines(edgeData, width, height) {
    const lines = [];
    const accumulator = new Map();
    const rhoStep = 2;
    const thetaStep = Math.PI / 90; // 2 degrees
    
    // Hough transform with optimizations
    for (let y = 0; y < height; y += 2) { // Skip pixels for performance
      for (let x = 0; x < width; x += 2) {
        if (edgeData[y * width + x] > 0) {
          for (let theta = 0; theta < Math.PI; theta += thetaStep) {
            const rho = Math.round((x * Math.cos(theta) + y * Math.sin(theta)) / rhoStep) * rhoStep;
            const key = `${rho},${Math.round(theta * 180 / Math.PI)}`;
            accumulator.set(key, (accumulator.get(key) || 0) + 1);
          }
        }
      }
    }
    
    // Extract strong lines
    const threshold = Math.max(20, width * height * 0.001);
    for (const [key, votes] of accumulator.entries()) {
      if (votes > threshold) {
        const [rho, thetaDeg] = key.split(',').map(Number);
        lines.push({
          rho: rho,
          theta: thetaDeg * Math.PI / 180,
          votes: votes
        });
      }
    }
    
    return lines.sort((a, b) => b.votes - a.votes);
  }

  findRectanglesFromLines(lines, width, height) {
    const rectangles = [];
    
    // Separate horizontal and vertical lines
    const horizontalLines = lines.filter(line => {
      const angle = Math.abs(line.theta * 180 / Math.PI);
      return (angle < 15 || angle > 165);
    }).slice(0, 6);
    
    const verticalLines = lines.filter(line => {
      const angle = Math.abs(line.theta * 180 / Math.PI);
      return (angle > 75 && angle < 105);
    }).slice(0, 6);
    
    // Find intersections to form rectangles
    for (let i = 0; i < horizontalLines.length - 1; i++) {
      for (let j = i + 1; j < horizontalLines.length; j++) {
        for (let k = 0; k < verticalLines.length - 1; k++) {
          for (let l = k + 1; l < verticalLines.length; l++) {
            const rect = this.getIntersectionRectangle(
              horizontalLines[i], horizontalLines[j],
              verticalLines[k], verticalLines[l],
              width, height
            );
            
            if (rect && this.isValidBusinessCard(rect, width, height)) {
              rectangles.push({
                ...rect,
                confidence: this.calculateConfidence(rect, width, height)
              });
            }
          }
        }
      }
    }
    
    return rectangles;
  }

  getIntersectionRectangle(h1, h2, v1, v2, imgWidth, imgHeight) {
    const intersections = [];
    
    // Calculate all four intersection points
    const lines = [h1, h2, v1, v2];
    for (let i = 0; i < 4; i++) {
      for (let j = i + 1; j < 4; j++) {
        const point = this.lineIntersection(lines[i], lines[j]);
        if (point && point.x >= 0 && point.x <= imgWidth && 
            point.y >= 0 && point.y <= imgHeight) {
          intersections.push(point);
        }
      }
    }
    
    if (intersections.length < 4) return null;
    
    // Find bounding rectangle
    const xs = intersections.map(p => p.x);
    const ys = intersections.map(p => p.y);
    
    return {
      x: Math.max(0, Math.min(...xs)),
      y: Math.max(0, Math.min(...ys)),
      width: Math.max(...xs) - Math.min(...xs),
      height: Math.max(...ys) - Math.min(...ys)
    };
  }

  lineIntersection(line1, line2) {
    const rho1 = line1.rho;
    const theta1 = line1.theta;
    const rho2 = line2.rho;
    const theta2 = line2.theta;
    
    const cos1 = Math.cos(theta1);
    const sin1 = Math.sin(theta1);
    const cos2 = Math.cos(theta2);
    const sin2 = Math.sin(theta2);
    
    const det = cos1 * sin2 - sin1 * cos2;
    if (Math.abs(det) < 0.01) return null;
    
    const x = (sin2 * rho1 - sin1 * rho2) / det;
    const y = (cos1 * rho2 - cos2 * rho1) / det;
    
    return { x, y };
  }

  isValidBusinessCard(rect, imgWidth, imgHeight) {
    const aspectRatio = rect.width / rect.height;
    const area = rect.width * rect.height;
    const imageArea = imgWidth * imgHeight;
    const areaRatio = area / imageArea;
    
    // Business card aspect ratio: 3.5" x 2" = 1.75, but allow some flexibility
    const isGoodAspectRatio = (aspectRatio > 1.3 && aspectRatio < 2.2) || 
                             (aspectRatio > 0.45 && aspectRatio < 0.77);
    
    const isGoodSize = areaRatio > 0.08 && areaRatio < 0.85;
    const isMinimumSize = rect.width > 60 && rect.height > 35;
    
    return isGoodAspectRatio && isGoodSize && isMinimumSize;
  }

  calculateConfidence(rect, imgWidth, imgHeight) {
    const aspectRatio = rect.width / rect.height;
    const area = rect.width * rect.height;
    const imageArea = imgWidth * imgHeight;
    const areaRatio = area / imageArea;
    
    // Ideal business card aspect ratio
    const targetAspectRatio = 1.75;
    const aspectScore = Math.max(0, 1 - Math.abs(aspectRatio - targetAspectRatio) / targetAspectRatio);
    
    // Ideal size (around 25% of image)
    const idealAreaRatio = 0.25;
    const sizeScore = Math.max(0, 1 - Math.abs(areaRatio - idealAreaRatio) / idealAreaRatio);
    
    // Position score (prefer centered)
    const centerX = rect.x + rect.width / 2;
    const centerY = rect.y + rect.height / 2;
    const imgCenterX = imgWidth / 2;
    const imgCenterY = imgHeight / 2;
    const distanceFromCenter = Math.sqrt(
      Math.pow((centerX - imgCenterX) / imgWidth, 2) + 
      Math.pow((centerY - imgCenterY) / imgHeight, 2)
    );
    const positionScore = Math.max(0, 1 - distanceFromCenter);
    
    return aspectScore * 0.5 + sizeScore * 0.3 + positionScore * 0.2;
  }

  // Main detection method with stability checking
  async detectBusinessCard(videoElement) {
    if (this.isProcessing || !videoElement || videoElement.videoWidth === 0) {
      return this.lastDetection || { detected: false, confidence: 0, bounds: null };
    }
    
    this.isProcessing = true;
    
    try {
      const width = videoElement.videoWidth;
      const height = videoElement.videoHeight;
      
      // Use smaller canvas for faster processing
      const scale = 0.25;
      const smallWidth = Math.floor(width * scale);
      const smallHeight = Math.floor(height * scale);
      
      this.detectionCtx.drawImage(videoElement, 0, 0, smallWidth, smallHeight);
      const imageData = this.detectionCtx.getImageData(0, 0, smallWidth, smallHeight);
      
      // Convert to grayscale
      const grayData = this.toGrayscale(imageData);
      
      // Detect edges
      const edges = this.detectCardEdges(grayData);
      
      // Find rectangles
      const rectangles = this.findRectangularContours(edges);
      
      let result = { detected: false, confidence: 0, bounds: null };
      
      if (rectangles.length > 0) {
        const bestRect = rectangles[0];
        
        // Scale back up to original size
        const scaledRect = {
          x: bestRect.x / scale,
          y: bestRect.y / scale,
          width: bestRect.width / scale,
          height: bestRect.height / scale
        };
        
        result = {
          detected: bestRect.confidence > this.confidenceThreshold,
          confidence: bestRect.confidence,
          bounds: scaledRect
        };
      }
      
      // Add to history for stability
      this.detectionHistory.push(result);
      if (this.detectionHistory.length > this.stabilityThreshold) {
        this.detectionHistory.shift();
      }
      
      // Check for stable detection
      const stableDetection = this.getStableDetection();
      this.lastDetection = stableDetection;
      
      return stableDetection;
      
    } catch (error) {
      return { detected: false, confidence: 0, bounds: null };
    } finally {
      this.isProcessing = false;
    }
  }

  getStableDetection() {
    if (this.detectionHistory.length < 3) {
      return this.detectionHistory[this.detectionHistory.length - 1] || 
             { detected: false, confidence: 0, bounds: null };
    }
    
    const recentDetections = this.detectionHistory.slice(-3);
    const detectedCount = recentDetections.filter(d => d.detected).length;
    
    if (detectedCount >= 2) {
      // Return the detection with highest confidence
      return recentDetections
        .filter(d => d.detected)
        .sort((a, b) => b.confidence - a.confidence)[0];
    }
    
    return { detected: false, confidence: 0, bounds: null };
  }

  // Crop the detected business card
  async cropBusinessCard(videoElement, bounds = null) {
    if (!videoElement) return null;
    
    try {
      // If no bounds provided, detect them
      let cropBounds = bounds;
      if (!cropBounds) {
        const detection = await this.detectBusinessCard(videoElement);
        if (!detection.detected || !detection.bounds) {
          return null;
        }
        cropBounds = detection.bounds;
      }
      
      // Create crop canvas
      const cropCanvas = document.createElement('canvas');
      const cropCtx = cropCanvas.getContext('2d');
      
      cropCanvas.width = cropBounds.width;
      cropCanvas.height = cropBounds.height;
      
      // Draw cropped region with some padding
      const padding = 5;
      cropCtx.drawImage(
        videoElement,
        Math.max(0, cropBounds.x - padding),
        Math.max(0, cropBounds.y - padding),
        cropBounds.width + padding * 2,
        cropBounds.height + padding * 2,
        0, 0,
        cropBounds.width,
        cropBounds.height
      );
      
      // Enhance the cropped image
      this.enhanceImage(cropCtx, cropCanvas.width, cropCanvas.height);
      
      return new Promise((resolve) => {
        cropCanvas.toBlob((blob) => {
          resolve({
            canvas: cropCanvas,
            blob: blob,
            dataUrl: cropCanvas.toDataURL('image/jpeg', 0.95),
            bounds: cropBounds,
            confidence: this.lastDetection?.confidence || 0.8
          });
        }, 'image/jpeg', 0.95);
      });
      
    } catch (error) {
      return null;
    }
  }

  enhanceImage(ctx, width, height) {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    
    // Simple contrast and brightness enhancement
    const contrast = 1.2;
    const brightness = 10;
    
    for (let i = 0; i < data.length; i += 4) {
      // Apply contrast and brightness
      data[i] = Math.min(255, Math.max(0, (data[i] - 128) * contrast + 128 + brightness));
      data[i + 1] = Math.min(255, Math.max(0, (data[i + 1] - 128) * contrast + 128 + brightness));
      data[i + 2] = Math.min(255, Math.max(0, (data[i + 2] - 128) * contrast + 128 + brightness));
    }
    
    ctx.putImageData(imageData, 0, 0);
  }

  // Clear detection history
  reset() {
    this.detectionHistory = [];
    this.lastDetection = null;
  }
}

export default ModernCardDetection;